import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, faqSchema } from "@/lib/firm";
import {
  BUSINESS_LOCATIONS,
  getBusinessLocation,
  type BusinessLocation,
} from "@/lib/business-locations";

export const Route = createFileRoute("/business-lawyers/$suburb")({
  loader: ({ params }) => {
    const content = getBusinessLocation(params.suburb);
    if (!content) throw notFound();
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.description) },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: `/business-lawyers/${params.suburb}` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `https://coastsidelaw.com.au/business-lawyers/${params.suburb}` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(content.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `${FIRM.name} — business lawyers, ${content.name}`,
            description: content.description,
            telephone: FIRM.phone,
            address: {
              "@type": "PostalAddress",
              streetAddress: FIRM.street,
              addressLocality: FIRM.suburb,
              addressRegion: FIRM.state,
              postalCode: FIRM.postcode,
              addressCountry: "AU",
            },
            areaServed: [content.name, ...content.nearby].map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            serviceType: [
              "Business sale and purchase",
              "Commercial conveyancing",
              "Commercial lease and lease assignment",
            ],
          }),
        },
      ],
    };
  },
  component: BusinessLocationPage,
});

function BusinessLocationPage() {
  const { content } = Route.useLoaderData() as { content: BusinessLocation };

  return (
    <>
      <PageHero
        eyebrow={`Business lawyers — ${content.name}`}
        title={content.title}
        lead={content.lead}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        {content.paragraphs.map((p) => (
          <p key={p} className="mb-5 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}

        <div className="mt-8 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Free download</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Our Queensland business sale and purchase checklist sets out the documents to request
            and the checks to complete before you sign and before you settle.
          </p>
          <Link
            to="/business-sale-checklist"
            className="mt-5 inline-block text-sm font-semibold text-primary underline"
          >
            Get the business sale checklist
          </Link>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What we watch for in {content.name} business deals
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.localNotes.map((n) => (
            <div key={n.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{n.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          {content.name} businesses we act for
        </h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {content.sectors.map((s) => (
            <li
              key={s}
              className="rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
            >
              {s}
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Guides worth reading first</h2>
        <ul className="mt-5 space-y-2 text-sm">
          {[
            {
              slug: "buying-or-selling-a-business-in-queensland",
              t: "Buying or selling a business in Queensland",
            },
            {
              slug: "business-sale-step-by-step-queensland",
              t: "Business sale step by step: from offer to settlement",
            },
            {
              slug: "commercial-property-conveyancing-queensland",
              t: "Commercial property conveyancing in Queensland",
            },
            {
              slug: "buying-and-selling-body-corporate-management-rights",
              t: "Buying and selling body corporate management rights",
            },
          ].map((g) => (
            <li key={g.slug}>
              <Link
                to="/guides/$slug"
                params={{ slug: g.slug }}
                className="font-semibold text-primary underline"
              >
                {g.t}
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Nearby areas we cover</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We act for business owners across {content.nearby.join(", ")} and the wider Caloundra
          district from our office at {FIRM_ADDRESS}.
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {BUSINESS_LOCATIONS.filter((l) => l.slug !== content.slug).map((l) => (
            <li key={l.slug}>
              <Link
                to="/business-lawyers/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                {l.name} business lawyers
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          {content.name} business law FAQs
        </h2>
        <div className="mt-6">
          <FaqList items={content.faqs} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading={`Talk to us about your ${content.name} business deal`} />
        </div>
      </section>

      <CtaBand
        title="Send us the contract before you sign"
        body={`We will review it, tell you what needs to change and quote a fixed fee before doing any work. Call ${FIRM.phone}.`}
      />
    </>
  );
}
